/**
 * Excel's worksheet name constraints (§9.7):
 * - Max 31 characters
 * - Forbidden characters: `: \ / ? * [ ]`
 */
const EXCEL_MAX_SHEET_NAME_LENGTH = 31;
const EXCEL_FORBIDDEN_SHEET_NAME_CHARS = /[:\\/?*[\]]/g;

/**
 * De-duplicate sheet names by appending `(2)`, `(3)`, ... to repeats.
 *
 * Must run **before** Excel sanitization (mapping §7.3, §9.7) — two long custom-tab names that
 * would truncate identically (e.g. `"Some long tab name A"` and `"Some long tab name B"` if both
 * exceed 31 chars before their disambiguating suffix) need their `(N)` markers in place before
 * any truncation strips them.
 */
export const dedupeSheetNames = (names: readonly string[]): string[] => {
	const seen = new Map<string, number>();
	return names.map((name) => {
		const count = seen.get(name) ?? 0;
		seen.set(name, count + 1);
		return count === 0 ? name : `${name} (${count + 1})`;
	});
};

/**
 * Truncate to Excel's 31-char limit and replace forbidden characters with `-` (mapping §9.7).
 *
 * Applied per name **after** `dedupeSheetNames` so the dedup `(N)` suffixes are preserved when
 * possible (they may themselves get truncated if the name is at the limit).
 */
export const sanitizeSheetName = (name: string): string => {
	return name.substring(0, EXCEL_MAX_SHEET_NAME_LENGTH).replace(EXCEL_FORBIDDEN_SHEET_NAME_CHARS, "-");
};

/**
 * The standard pipeline: dedupe → sanitize. Returns a same-length array of safe sheet names.
 */
export const finalizeSheetNames = (rawNames: readonly string[]): string[] => {
	return dedupeSheetNames(rawNames).map(sanitizeSheetName);
};
